import React from 'react';
import { Clock, MapPin, Building2, CheckCircle, XCircle, AlertCircle, Briefcase, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMyApplications } from '../features/opportunities/hooks/use-opportunities';
import { timeAgo } from '../utils/timeAgo';
import { usePanelByOpportunityId } from '../features/panels/hooks/use-panels';

const STATUS_CONFIG = {
  pending: {
    label: 'Under Review',
    icon: AlertCircle,
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
  },
  accepted: {
    label: 'Accepted',
    icon: CheckCircle,
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
  rejected: {
    label: 'Not Selected',
    icon: XCircle,
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
  },
};

// Individual card — needs to call hook at component level (not inside map)
const ApplicationCard = ({ application }) => {
  const navigate = useNavigate();
  const { opportunity, status, createdAt } = application;
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;

  const { data: panelData } = usePanelByOpportunityId(opportunity?.id);
  const panel = panelData; // interceptor already unwrapped { success, data: panel }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900 leading-tight mb-1">
            {opportunity?.title || 'Unknown Position'}
          </h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Building2 size={14} className="text-gray-400" /> {opportunity?.clinicName}
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={14} className="text-gray-400" /> {opportunity?.location}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} className="text-gray-400" /> Applied {timeAgo(createdAt)}
            </span>
          </div>
        </div>

        {/* Status Badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold flex-shrink-0 ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
          <StatusIcon size={14} />
          {cfg.label}
        </div>
      </div>

      {/* Status message */}
      <div className="p-4 rounded-xl mb-4 bg-gray-50 border border-gray-100">
        <p className="text-sm leading-relaxed text-gray-600 font-medium">
          {status === 'pending' && 'Your application is being reviewed. The hiring team will contact you with next steps.'}
          {status === 'accepted' && '🎉 Congratulations! You\'ve been accepted. The clinic will reach out to you shortly to discuss next steps.'}
          {status === 'rejected' && 'Thank you for your interest. The position has been filled by another candidate.'}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400 font-medium">
          {opportunity?.type && (
            <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full">{opportunity.type}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Panel link if panel exists for this opportunity */}
          {panel && (
            <button
              onClick={() => navigate(`/panels/${panel.id}`)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              View Panel <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Skeleton ───────────────────────────────────────────────────────────────────
const ApplicationSkeleton = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm animate-pulse">
    <div className="flex justify-between mb-4">
      <div>
        <div className="h-5 bg-gray-200 rounded w-52 mb-2" />
        <div className="flex gap-4">
          <div className="h-3 bg-gray-100 rounded w-28" />
          <div className="h-3 bg-gray-100 rounded w-20" />
        </div>
      </div>
      <div className="h-8 bg-gray-100 rounded-full w-28" />
    </div>
    <div className="h-16 bg-gray-50 rounded-xl mb-4" />
    <div className="flex justify-between">
      <div className="h-6 bg-gray-100 rounded-full w-16" />
      <div className="h-8 bg-gray-200 rounded-lg w-28" />
    </div>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────
const MyApplicationsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMyApplications();
  const applications = data?.data || [];

  return (
    <AppShell>
      <div className="flex-1 max-w-4xl mx-auto w-full px-8 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">My Applications</h1>
            <p className="text-sm text-gray-500">Track the status of all your job applications</p>
          </div>
          <button
            onClick={() => navigate('/opportunities')}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
          >
            Browse Jobs <ArrowRight size={16} />
          </button>
        </div>



        {/* Loading */}
        {isLoading && (
          <div className="space-y-5">
            {[1, 2, 3].map((i) => <ApplicationSkeleton key={i} />)}
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="text-center py-16 text-red-500 text-sm">Error loading applications.</div>
        )}

        {/* Empty state */}
        {!isLoading && applications.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Briefcase size={28} className="text-[#6b46c1]" />
            </div>
            <p className="text-gray-900 font-semibold text-lg mb-1">No applications yet</p>
            <p className="text-sm text-gray-500 mb-6">Start applying to veterinary positions to track them here</p>
            <button
              onClick={() => navigate('/opportunities')}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-[#6b46c1] rounded-xl hover:bg-[#553098] transition-colors"
            >
              Browse Opportunities
            </button>
          </div>
        )}

        {/* Application cards */}
        {!isLoading && applications.length > 0 && (
          <div className="space-y-5">
            {applications.map((app) => (
              <ApplicationCard key={app.id} application={app} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default MyApplicationsPage;
